import { CompactUser } from 'src/context/user/types';
import { getLocalStorageEntry, setLocalStorageEntry } from 'src/utils/webStorageUtils';

export const PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL = 10;
export const PRESENTATION_SCREEN_PRONUNCIATION_TOKEN = 'presentation_screen_pronunciation' as const;

const STORAGE_PREFIX = 'presentation-pronunciation-free-learnables';

type DailyUsage = {
  date: string;
  consumed: number;
};

const todayKey = () => new Date().toISOString().slice(0, 10);

export const getPresentationPronunciationStorageKey = (userId: number): string =>
  `${STORAGE_PREFIX}-${userId}`;

const readUsage = (userId: number): DailyUsage => {
  const raw = getLocalStorageEntry(getPresentationPronunciationStorageKey(userId));
  if (!raw) {
    return { date: todayKey(), consumed: 0 };
  }

  try {
    const parsed = JSON.parse(raw) as DailyUsage & { learnableIds?: number[] };
    if (parsed?.date !== todayKey()) {
      return { date: todayKey(), consumed: 0 };
    }

    if (typeof parsed.consumed === 'number') {
      return { date: parsed.date, consumed: parsed.consumed };
    }

    if (Array.isArray(parsed.learnableIds)) {
      return { date: parsed.date, consumed: parsed.learnableIds.length };
    }

    return { date: todayKey(), consumed: 0 };
  } catch {
    return { date: todayKey(), consumed: 0 };
  }
};

const writeUsage = (userId: number, usage: DailyUsage) => {
  setLocalStorageEntry(getPresentationPronunciationStorageKey(userId), JSON.stringify(usage));
};

export const getPresentationPronunciationAttemptsRemaining = (
  user: CompactUser | null | undefined,
): number => {
  if (!user?.id || user.isPro) {
    return PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL;
  }

  return Math.max(0, PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL - readUsage(user.id).consumed);
};

/** Records one consumed presentation-pronunciation token for today. */
export const recordPresentationPronunciationConsumption = (
  user: CompactUser | null | undefined,
): number => {
  if (!user?.id || user.isPro) {
    return PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL;
  }

  const usage = readUsage(user.id);
  usage.consumed += 1;
  writeUsage(user.id, usage);

  return Math.max(0, PRESENTATION_PRONUNCIATION_DAILY_FREE_TOTAL - usage.consumed);
};
